import styled from 'styled-components';
import { useState } from 'react';

import { Button } from '@/ui/Button';
import uploadImage from './upload.svg';
import { DocumentVideoUploadButton } from '@/component/FileUpload/DocumentVideoUploadButton';
import { FileUploader } from '@/component/FileUpload/types';
import { ViewPracticalAssessmentVideoUploadInProgress } from '@/administration/pages/Course/pages/assessments/practicalAssessmentVideo/variants/View/ViewPracticalAssessmentVideoUploadInProgress';
import { useVideoUploadProgress } from '@/component/customEditorV2/ui/elements/Video/hooks/useVideoUploadProgress';
import { ViewPracticalAssessmentVideoLinkModal } from '@/administration/pages/Course/pages/assessments/practicalAssessmentVideo/variants/View/ViewPracticalAssessmentVideoLinkModal';

type ViewPracticalAssessmentVideoSumbissionFormProps = {
  onLinkSubmit: (link: string) => void;
  videoUpload: FileUploader<'video'>;
};

const VideoImage = styled.img.attrs({ src: uploadImage })``;

const RootStyled = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
  flex: 1;
  justify-content: center;
  border: 1px dashed ${({ theme }) => theme.colors.neutral.outline1};
  border-radius: 12px;
  width: 100%;
  overflow: hidden;
`;

const StyledHeader = styled.span`
  color: ${({ theme }) => theme.colors.neutral.onBackground};
  ${({ theme }) => theme.typography.title3};
`;

const ActionsStyled = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ActionsSeparatorLabelStyled = styled.div`
  ${({ theme }) => theme.typography.overline};
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.neutral.onBackground};
`;

export const ViewPracticalAssessmentVideoSumbissionForm = ({
  videoUpload,
  onLinkSubmit,
}: ViewPracticalAssessmentVideoSumbissionFormProps): JSX.Element => {
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);

  const transcodingInProgress = videoUpload.uploading && videoUpload.uploadingState === 'Transcoding';

  const { current: currentProgress, prev: prevProgress } = useVideoUploadProgress({
    transcodingInProgress,
    vimeoProgress: videoUpload.uploadingProgress,
    maxVisualUploadingProgress: 50,
  });

  if (videoUpload.uploading) {
    return <ViewPracticalAssessmentVideoUploadInProgress prevProgress={prevProgress} currentProgress={currentProgress} />;
  }

  return (
    <>
      <ViewPracticalAssessmentVideoLinkModal
        open={isLinkModalOpen}
        onClose={() => {
          setIsLinkModalOpen(false);
        }}
        onSubmit={onLinkSubmit}
      />
      <RootStyled>
        <VideoImage />
        <StyledHeader>Add video</StyledHeader>
        <ActionsStyled>
          <DocumentVideoUploadButton
            icon={['far', 'file-upload']}
            $loading={!!videoUpload.uploading}
            variant="secondary"
            size="large"
            uploadFile={videoUpload.uploadFile}
            fileType={'video'}
          >
            Upload
          </DocumentVideoUploadButton>
          <ActionsSeparatorLabelStyled>OR</ActionsSeparatorLabelStyled>
          <Button
            icon={'link'}
            title="Paste link"
            variant={'secondary'}
            onClick={() => {
              setIsLinkModalOpen(true);
            }}
          >
            Paste link
          </Button>
        </ActionsStyled>
      </RootStyled>
    </>
  );
};

ViewPracticalAssessmentVideoSumbissionForm.displayName = 'VideoUploader';
